import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, imageUrl, videoUrl } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  ArrowLeft, UsersThree, UploadSimple, SpinnerGap, PlayCircle, LinkSimple, Check,
  UserPlus, Trash, Globe, X, PaperPlaneTilt, Images,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ShareButton from "@/components/ShareButton";
import AddToAlbumDialog from "@/components/AddToAlbumDialog";
import AlbumMemories from "@/components/AlbumMemories";
import { CheckSquare, Sparkle } from "@phosphor-icons/react";

export default function SharedAlbumDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("contributor");
  const [copied, setCopied] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [memoriesOpen, setMemoriesOpen] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/shared-albums/${id}`).then((r) => setD(r.data)).catch(() => nav("/albums"));
  useEffect(() => { load(); }, [id]);

  const album = d?.album;
  const canContribute = d && (d.my_role === "owner" || d.my_role === "contributor" || d.my_role === "admin");
  const isOwner = d?.is_owner;
  const joinUrl = album ? `${window.location.origin}/join/${album.join_token}` : "";

  const onFiles = async (files) => {
    setUploading(true);
    let ok = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post(`/shared-albums/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    }
    setUploading(false);
    if (ok) { toast.success(`Added ${ok} item(s)`); load(); }
  };

  const invite = async () => {
    if (!email.trim()) { toast.error("Enter an email"); return; }
    try {
      const { data } = await api.post(`/shared-albums/${id}/invite`, { email: email.trim(), role });
      toast.success(`${data.member.name} added as ${data.member.role}`);
      setEmail(""); setInviteOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not invite"); }
  };

  const togglePublic = async () => {
    const v = album.visibility === "public" ? "members" : "public";
    await api.patch(`/shared-albums/${id}`, { visibility: v });
    toast.success(v === "public" ? "Album is now public" : "Album is private to members");
    load();
  };

  const removeMember = async (uid) => {
    await api.delete(`/shared-albums/${id}/members/${uid}`);
    toast.success("Member removed"); load();
  };

  const copyJoin = () => { navigator.clipboard?.writeText(joinUrl); setCopied(true); setTimeout(() => setCopied(false), 1600); toast.success("Invite link copied"); };

  const del = async () => {
    if (!window.confirm("Delete this album? Photos stay in each contributor's library.")) return;
    await api.delete(`/shared-albums/${id}`); toast.success("Album deleted"); nav("/albums");
  };

  const removeFromAlbum = async (pid) => {
    try {
      const { data } = await api.post(`/shared-albums/${id}/remove-photos`, { photo_ids: [pid] });
      if (data.removed) { toast.success("Removed from album"); load(); }
      else toast.error("You can only remove your own photos");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  const toggleSel = (pid) => setSelected((s) => { const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid); return n; });
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };
  const bulkRemove = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} photo(s) from this album? They stay in your library.`)) return;
    try {
      const { data } = await api.post(`/shared-albums/${id}/remove-photos`, { photo_ids: [...selected] });
      toast.success(`Removed ${data.removed} from album`); exitSelect(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  if (!d) return <div className="grid h-[60vh] place-items-center"><SpinnerGap size={30} className="animate-spin text-[#F27A54]" /></div>;

  return (
    <div className="mx-auto max-w-6xl">
      <button onClick={() => nav("/albums")} data-testid="album-back" className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-[#787167] hover:text-[#F27A54]">
        <ArrowLeft size={16} weight="bold" /> Shared albums
      </button>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl">
        <div className="h-44 sm:h-56">
          {album.cover_url ? <img src={album.cover_url} alt="" className="h-full w-full object-cover" /> : <div className="brand-sunset h-full w-full" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10" />
        </div>
        <div className="absolute bottom-4 left-5 right-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-black text-white drop-shadow">{album.title}</h1>
            {album.description && <p className="mt-1 max-w-xl text-sm text-white/85">{album.description}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {d.photos.length > 0 && (
              <Button data-testid="album-memories-btn" onClick={() => setMemoriesOpen(true)}
                className="h-10 gap-1.5 rounded-full bg-gradient-to-r from-[#F27A54] to-[#F59E0B] text-white hover:opacity-90">
                <Sparkle size={16} weight="fill" /> Memories
              </Button>
            )}
            {canContribute && (
              <Button data-testid="album-add-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="h-10 gap-1.5 rounded-full bg-white/90 text-[#2C2724] hover:bg-white">
                {uploading ? <SpinnerGap size={16} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />} Add photos
              </Button>
            )}
            <input ref={fileRef} type="file" multiple accept="image/*,video/*,.heic,.mp4,.mov" className="hidden" data-testid="album-file-input" onChange={(e) => onFiles(e.target.files)} />
            {album.visibility === "public" && <ShareButton id={album.id} type="album" title={album.title} variant="button" />}
            {isOwner && (
              <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="album-invite-btn" className="h-10 gap-1.5 rounded-full bg-[#F27A54] text-white hover:bg-[#d9633f]"><UserPlus size={16} weight="bold" /> Invite</Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader><DialogTitle className="font-display text-xl">Invite people</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="overline mb-1 block text-[10px] text-[#787167]">By email (registered users)</label>
                      <div className="flex gap-2">
                        <input data-testid="invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="friend@email.com"
                          className="h-10 flex-1 rounded-xl border border-[#E5DFD3] px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
                        <select data-testid="invite-role" value={role} onChange={(e) => setRole(e.target.value)} className="h-10 rounded-xl border border-[#E5DFD3] px-2 text-sm">
                          <option value="contributor">Contributor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      </div>
                      <Button data-testid="invite-submit" onClick={invite} className="mt-2 h-10 w-full gap-1.5 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245840]"><PaperPlaneTilt size={14} weight="fill" /> Send invite</Button>
                    </div>
                    <div className="border-t border-[#E5DFD3] pt-4">
                      <label className="overline mb-2 block text-[10px] text-[#787167]">Or share a join link / QR</label>
                      <div className="flex items-center gap-3">
                        <div className="rounded-xl border border-[#E5DFD3] bg-white p-2"><QRCodeCanvas value={joinUrl} size={92} fgColor="#2C2724" data-testid="join-qr" /></div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate rounded-lg bg-[#F0EBE1] px-3 py-2 text-xs text-[#5C554D]">{joinUrl}</p>
                          <Button data-testid="copy-join" onClick={copyJoin} variant="outline" className="mt-2 h-9 w-full gap-1.5 rounded-full">
                            {copied ? <Check size={14} weight="bold" className="text-[#2D6A4F]" /> : <LinkSimple size={14} weight="bold" />}{copied ? "Copied" : "Copy link"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_300px]">
        {/* Photos masonry */}
        <div className="min-w-0">
          {canContribute && d.photos.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {!selectMode ? (
                <button data-testid="album-select-mode" onClick={() => setSelectMode(true)}
                  className="flex items-center gap-1.5 rounded-full border border-[#E5DFD3] px-3 py-1.5 text-xs font-bold text-[#5C554D] hover:border-[#F27A54] hover:text-[#F27A54]">
                  <CheckSquare size={14} weight="bold" /> Select
                </button>
              ) : (
                <>
                  <span className="text-xs font-bold text-[#5C554D]" data-testid="album-select-count">{selected.size} selected</span>
                  <button data-testid="album-copy-move" onClick={() => selected.size ? setMoveDialogOpen(true) : toast.error("Select photos first")}
                    className="flex items-center gap-1.5 rounded-full bg-[#2C2724] px-3 py-1.5 text-xs font-bold text-white hover:bg-black disabled:opacity-50" disabled={!selected.size}>
                    <Images size={14} weight="fill" /> Copy / Move to album
                  </button>
                  <button data-testid="album-bulk-remove" onClick={bulkRemove} disabled={!selected.size}
                    className="flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50 disabled:opacity-50">
                    <Trash size={14} weight="bold" /> Remove
                  </button>
                  <button data-testid="album-select-cancel" onClick={exitSelect} className="rounded-full px-3 py-1.5 text-xs font-semibold text-[#787167] hover:text-[#2C2724]">Cancel</button>
                </>
              )}
            </div>
          )}
          {d.photos.length === 0 ? (
            <div className="grid place-items-center rounded-3xl border border-dashed border-[#E5DFD3] p-14 text-center">
              <UploadSimple size={40} weight="thin" className="text-[#D9C9B3]" />
              <p className="mt-3 font-display font-bold">No photos yet</p>
              <p className="text-sm text-[#787167]">{canContribute ? "Tap “Add photos” to start the album." : "Contributors haven't added anything yet."}</p>
            </div>
          ) : (
            <div className="columns-2 gap-4 md:columns-3 [&>*]:mb-4" data-testid="album-photo-grid">
              {d.photos.map((p) => {
                const sel = selected.has(p.id);
                return (
                <div key={p.id} data-testid={`album-photo-${p.id}`}
                  className={`group relative block break-inside-avoid overflow-hidden rounded-2xl ${selectMode && sel ? "ring-4 ring-[#F27A54]" : ""}`}>
                  {selectMode ? (
                    <button onClick={() => toggleSel(p.id)} className="block w-full" data-testid={`album-photo-toggle-${p.id}`}>
                      <img src={imageUrl(p.id, "display")} alt="" loading="lazy" className="w-full object-cover" />
                      <span className={`absolute left-2 top-2 grid h-6 w-6 place-items-center rounded-full border-2 ${sel ? "border-[#F27A54] bg-[#F27A54] text-white" : "border-white bg-black/30 text-transparent"}`}>
                        <Check size={13} weight="bold" />
                      </span>
                    </button>
                  ) : (
                    <>
                      <Link to={`/p/${p.id}`} className="block">
                        <img src={imageUrl(p.id, "display")} alt="" loading="lazy" className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        {p.media_type === "video" && <PlayCircle size={40} weight="fill" className="absolute inset-0 m-auto text-white/90 drop-shadow" />}
                        {p.photographer && <span className="absolute bottom-2 left-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-semibold text-white">{p.photographer}</span>}
                      </Link>
                      {canContribute && (
                        <button data-testid={`album-remove-photo-${p.id}`} onClick={() => removeFromAlbum(p.id)} title="Remove from album"
                          className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-black/55 text-white opacity-0 transition-opacity hover:bg-red-500 group-hover:opacity-100">
                          <X size={15} weight="bold" />
                        </button>
                      )}
                    </>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Members + settings */}
        <div className="min-w-0 space-y-4">
          <div className="rounded-3xl border border-[#E5DFD3] bg-white p-5">
            <div className="flex items-center gap-2"><UsersThree size={18} weight="fill" className="text-[#2D6A4F]" /><h3 className="font-display text-sm font-bold">Members ({album.members.length})</h3></div>
            <div className="mt-3 space-y-2">
              {album.members.map((m) => (
                <div key={m.user_id} data-testid={`member-${m.user_id}`} className="flex items-center justify-between rounded-xl bg-[#FAF8F5] px-3 py-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#F27A54] text-xs font-bold text-white">{(m.name || "?").slice(0, 1).toUpperCase()}</span>
                    <div className="min-w-0"><p className="truncate text-sm font-semibold">{m.name}</p><p className="text-[10px] uppercase tracking-wide text-[#787167]">{m.role}</p></div>
                  </div>
                  {isOwner && m.role !== "owner" && (
                    <button data-testid={`remove-member-${m.user_id}`} onClick={() => removeMember(m.user_id)} className="grid h-7 w-7 place-items-center rounded-full text-[#787167] hover:bg-red-50 hover:text-red-500"><X size={14} weight="bold" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {isOwner && (
            <div className="rounded-3xl border border-[#E5DFD3] bg-white p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Globe size={18} weight="fill" className="text-[#2D6A4F]" /><div><p className="font-display text-sm font-bold">Publish publicly</p><p className="text-xs text-[#787167]">Anyone with the link can view.</p></div></div>
                <Switch data-testid="album-public-toggle" checked={album.visibility === "public"} onCheckedChange={togglePublic} className="data-[state=checked]:bg-[#2D6A4F]" />
              </div>
              {album.visibility === "public" && (
                <Link to={`/albums/p/${album.id}`} className="mt-3 block break-all text-xs font-semibold text-[#F27A54] hover:underline" data-testid="public-album-link">
                  View public page → /albums/p/{album.id}
                </Link>
              )}
              <Button data-testid="album-delete-btn" onClick={del} variant="outline" className="mt-4 h-9 w-full gap-1.5 rounded-full border-red-200 text-red-500 hover:bg-red-50"><Trash size={14} weight="bold" /> Delete album</Button>
            </div>
          )}
        </div>
      </div>

      <AddToAlbumDialog
        open={moveDialogOpen} onOpenChange={setMoveDialogOpen}
        photoIds={[...selected]} sourceAlbumId={id}
        onDone={() => { exitSelect(); load(); }}
      />
      <AlbumMemories albumId={id} open={memoriesOpen} onOpenChange={setMemoriesOpen} />
    </div>
  );
}
